import React, { useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import remarkMath from 'remark-math';
import remarkEmoji from 'remark-emoji';
import remarkToc from 'remark-toc';
import rehypeKatex from 'rehype-katex';
import rehypeSlug from 'rehype-slug';
import rehypeRaw from 'rehype-raw';
import { visit } from 'unist-util-visit';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import { useNavigate } from 'react-router-dom';
import mermaid from 'mermaid';
import 'katex/dist/katex.min.css';
import '../../styles/markdown.css';
import FlowChartEmbed from '../Flowchart/FlowChartEmbed';

interface MarkdownRendererProps {
  content: string;
}

// Initialize mermaid
mermaid.initialize({
  startOnLoad: true,
  theme: 'dark',
  securityLevel: 'loose',
  fontFamily: 'inherit'
});

// Code block wrapper with copy button
const CodeBlockWithCopy: React.FC<{ code: string; language: string }> = ({ code, language }) => {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(code);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch (err) {
      console.error('Failed to copy code:', err);
    }
  };

  return (
    <div style={{ position: 'relative' }}>
      <button
        onClick={handleCopy}
        style={{
          position: 'absolute',
          top: '8px',
          right: '8px',
          padding: '6px 8px',
          background: copied ? '#28a745' : 'rgba(255, 255, 255, 0.1)',
          color: '#fff',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          borderRadius: '4px',
          cursor: 'pointer',
          fontSize: '16px',
          fontFamily: 'system-ui, -apple-system, sans-serif',
          transition: 'all 0.2s ease',
          zIndex: 10,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
        onMouseEnter={(e) => {
          if (!copied) {
            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.2)';
          }
        }}
        onMouseLeave={(e) => {
          if (!copied) {
            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.1)';
          }
        }}
        title={copied ? 'Copied!' : 'Copy to clipboard'}
      >
        {copied ? (
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2">
            <polyline points="4,8 7,11 12,5" />
          </svg>
        ) : (
          <svg width="16" height="16" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5">
            <rect x="5" y="5" width="9" height="9" rx="1" />
            <path d="M3 11V3a1 1 0 0 1 1-1h8" />
          </svg>
        )}
      </button>
      <SyntaxHighlighter
        style={vscDarkPlus as any}
        language={language}
        PreTag="div"
      >
        {code}
      </SyntaxHighlighter>
    </div>
  );
};

export const MarkdownRenderer: React.FC<MarkdownRendererProps> = ({ content }) => {
  const navigate = useNavigate();

  useEffect(() => {
    // Render mermaid diagrams after component mounts
    mermaid.contentLoaded();
  }, [content]);

  // Custom plugin to parse image attributes from markdown
  const remarkParseImageAttrs = () => {
    return (tree: any) => {
      visit(tree, 'image', (node) => {
        if (node.alt && typeof node.alt === 'string') {
          const attrMatch = node.alt.match(/^(.*)\{(.+)\}$/);
          if (attrMatch) {
            node.alt = attrMatch[1].trim();
            const attrString = attrMatch[2];
            
            const widthMatch = attrString.match(/width\s*=\s*(\d+%?)/);
            if (widthMatch) {
              node.data = node.data || {};
              node.data.hProperties = node.data.hProperties || {};
              node.data.hProperties.width = widthMatch[1];
            }
            
            const heightMatch = attrString.match(/height\s*=\s*(\d+%?)/);
            if (heightMatch) {
              node.data = node.data || {};
              node.data.hProperties = node.data.hProperties || {};
              node.data.hProperties.height = heightMatch[1];
            }
            
            const alignMatch = attrString.match(/align\s*=\s*(left|center|right)/);
            if (alignMatch) {
              node.data = node.data || {};
              node.data.hProperties = node.data.hProperties || {};
              node.data.hProperties.align = alignMatch[1];
            }
          }
        }
      });
    };
  };



  // Check if a URL is an internal document link
  const isInternalLink = (href: string | undefined): boolean => {
    if (!href) return false;
    // Check if it starts with /docs/ or is a relative path starting with docs/
    return href.startsWith('/docs/') || href.startsWith('docs/');
  };

  // Check if a URL is an anchor link (within the same page)
  const isAnchorLink = (href: string | undefined): boolean => {
    if (!href) return false;
    return href.startsWith('#');
  };

  // Pre-process markdown to convert [[flowchart:slug]] to a special component tag
  // First, extract code blocks and replace with placeholders
  const codeBlocks: string[] = [];
  const contentWithoutCode = content.replace(
    /```[\s\S]*?```/g,
    (match) => {
      codeBlocks.push(match);
      return `__CODE_BLOCK_${codeBlocks.length - 1}__`;
    }
  );
  
  // Now process flowchart syntax
  const processedContent = contentWithoutCode.replace(
    /\[\[flowchart:([^\]]+)\]\]/g,
    '<flowchart-embed slug="$1" />'
  ).replace(
    /__CODE_BLOCK_(\d+)__/g,
    (_, index) => codeBlocks[parseInt(index)]
  );

  return (
    <div className="markdown-body">
      <ReactMarkdown
        remarkPlugins={[remarkGfm, remarkMath, remarkEmoji, remarkParseImageAttrs, remarkToc]}
        rehypePlugins={[rehypeSlug, rehypeKatex, rehypeRaw]}
        components={{
          // @ts-ignore - custom HTML component
          'flowchart-embed': ({ slug }: { slug: string }) => {
            return <FlowChartEmbed slug={slug} />;
          },
          code({ node, className, children, ...props }: any) {
            const match = /language-(\w+)/.exec(className || '');
            const language = match ? match[1] : '';
            const inline = !className;
            const code = String(children).replace(/\n$/, '');

            // Handle mermaid diagrams
            if (language === 'mermaid') {
              return (
                <div className="mermaid">
                  {code}
                </div>
              );
            }

            // Regular code blocks with syntax highlighting and copy button
            if (!inline && language) {
              return <CodeBlockWithCopy code={code} language={language} />;
            }

            // Inline code
            return (
              <code className={className} {...props}>
                {children}
              </code>
            );
          },
          // Handle internal and external links differently
          a({ node, children, href, ...props }: any) {
            // Internal document links navigate in same tab
            if (isInternalLink(href)) {
              return (
                <a
                  href={href}
                  onClick={(e) => {
                    e.preventDefault();
                    if (href) {
                      navigate(href);
                    }
                  }}
                  {...props}
                >
                  {children}
                </a>
              );
            }
            // Anchor links - let browser handle scrolling naturally
            if (isAnchorLink(href)) {
              return (
                <a href={href} {...props}>
                  {children}
                </a>
              );
            }
            // External links open in new tab for security
            return (
              <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
                {children}
              </a>
            );
          },
          // Task list checkboxes
          input({ node, type, ...props }) {
            if (type === 'checkbox') {
              return <input type="checkbox" disabled {...props} />;
            }
            return <input type={type} {...props} />;
          },
          // Image with dimension and alignment support
          img({ node, src, alt, ...props }: any) {
            const imgStyle: React.CSSProperties = {
              maxWidth: '100%',
              borderRadius: '6px',
              display: 'block',
            };
            
            const wrapperStyle: React.CSSProperties = {};
            
            // Parse attributes from alt text if present (format: "alt text{width=100 height=200 align=center}")
            let parsedAlt = alt;
            let width: string | number | undefined;
            let height: string | number | undefined;
            let align: string | undefined;
            
            if (alt && typeof alt === 'string') {
              const attrMatch = alt.match(/^(.*)\{(.+)\}$/);
              if (attrMatch) {
                parsedAlt = attrMatch[1].trim();
                const attrString = attrMatch[2];
                
                // Parse width
                const widthMatch = attrString.match(/width\s*=\s*(\d+%?)/);
                if (widthMatch) {
                  width = widthMatch[1];
                }
                
                // Parse height
                const heightMatch = attrString.match(/height\s*=\s*(\d+%?)/);
                if (heightMatch) {
                  height = heightMatch[1];
                }
                
                // Parse align
                const alignMatch = attrString.match(/align\s*=\s*(left|center|right)/);
                if (alignMatch) {
                  align = alignMatch[1];
                }
              }
            }
            
            // Handle width
            if (width) {
              imgStyle.width = typeof width === 'string' ? width : `${width}px`;
              imgStyle.height = 'auto';
            }
            
            // Handle height
            if (height) {
              imgStyle.height = typeof height === 'string' ? height : `${height}px`;
              
              if (!width) {
                imgStyle.width = 'auto';
              }
            }
            
            // Handle alignment
            if (align) {
              if (align === 'center') {
                wrapperStyle.textAlign = 'center';
              } else if (align === 'right') {
                wrapperStyle.textAlign = 'right';
              } else if (align === 'left') {
                wrapperStyle.textAlign = 'left';
              }
            }
            
            // If alignment is specified, wrap in div
            if (align) {
              return (
                <div style={wrapperStyle}>
                  <img src={src} alt={parsedAlt} style={imgStyle} {...props} />
                </div>
              );
            }
            
            // No alignment - return bare img
            return <img src={src} alt={parsedAlt} style={imgStyle} {...props} />;
          }
        }}
      >
        {processedContent}
      </ReactMarkdown>
    </div>
  );
};
